import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { usePaywallInfoModal } from "@/react/components/CommunityOnboarding/NewChecklistComponents/PaywallInfoModal";
import { useAddSpaceModal } from "@/react/components/Modals/AddSpaceModal";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import { communitiesPaths } from "@/react/helpers/routerPathHelpers";
import { useChecklistApi } from "@/react/hooks/useChecklistApi";
import { useConfirmAccess } from "@/react/hooks/useConfirmAccessAction";
import { useCreateFirstBroadcast } from "@/react/hooks/useCreateFirstBroadcast";
import { useDefaultAccessGroup } from "@/react/hooks/useDefaultAccessGroup";
import type { StepAction, StepKey } from "@/react/types/Checklist";
import { stepKeys } from "@/react/types/Checklist";
import {
  memberProfileModalPathsWithState,
  settingsNavPathV3,
} from "@circle-react/helpers/urlHelpers";

interface ChecklistActions {
  stepActions: Record<StepKey, StepAction>;
}

export const useChecklistActions = (): ChecklistActions => {
  const history = useHistory();
  const location = useLocation();
  const gettingStartedPath = communitiesPaths.onboarding();

  const { updateChecklist } = useChecklistApi();
  const { confirmAccess } = useConfirmAccess();
  const { createFirstBroadcast } = useCreateFirstBroadcast();

  const markStepDone = useCallback(
    (step: StepKey) => updateChecklist({ [step]: true }),
    [updateChecklist],
  );

  const withStepCompletion = useCallback(
    (stepKey: StepKey, action: StepAction) => async () => {
      try {
        await action();
        markStepDone(stepKey);
      } catch (error) {
        notifyBugsnag(JSON.stringify({ error: String(error) }));
      }
    },
    [markStepDone],
  );

  const spaceModal = useAddSpaceModal();
  const paywallInfoModal = usePaywallInfoModal();

  const { openEditAccessGroupModal } = useDefaultAccessGroup();

  const actions = useMemo<Record<StepKey, () => void | Promise<void>>>(
    () => ({
      [stepKeys.account_created]: () => {
        history.push(gettingStartedPath);
      },

      [stepKeys.personalize_profile]: withStepCompletion(
        stepKeys.personalize_profile,
        () => {
          history.push(memberProfileModalPathsWithState.profile(location));
        },
      ),

      [stepKeys.customize_branding]: withStepCompletion(
        stepKeys.customize_branding,
        () => {
          history.push(settingsNavPathV3.content.theme);
        },
      ),

      [stepKeys.create_spaces]: withStepCompletion(
        stepKeys.create_spaces,
        () => {
          void spaceModal.show();
        },
      ),

      [stepKeys.confirm_access]: withStepCompletion(
        stepKeys.confirm_access,
        () => {
          void confirmAccess();
        },
      ),

      [stepKeys.setup_payments]: withStepCompletion(
        stepKeys.setup_payments,
        () => {
          void paywallInfoModal.show();
        },
      ),

      [stepKeys.add_members]: withStepCompletion(stepKeys.add_members, () => {
        history.push(
          `${settingsNavPathV3.members.invite}?from=${gettingStartedPath}`,
        );
      }),

      [stepKeys.create_invite_link]: withStepCompletion(
        stepKeys.create_invite_link,
        () => {
          history.push({
            pathname: settingsNavPathV3.members.new_invitation_link_details,
            state: { from: gettingStartedPath },
          });
        },
      ),

      [stepKeys.create_access_group]: withStepCompletion(
        stepKeys.create_access_group,
        openEditAccessGroupModal,
      ),

      [stepKeys.import_email_list]: withStepCompletion(
        stepKeys.import_email_list,
        () => {
          history.push(
            `${settingsNavPathV3.members.import_contacts}?from=${gettingStartedPath}`,
          );
        },
      ),

      [stepKeys.send_launch_announcement]: withStepCompletion(
        stepKeys.send_launch_announcement,
        createFirstBroadcast,
      ),
    }),
    [
      openEditAccessGroupModal,
      history,
      location,
      withStepCompletion,
      paywallInfoModal,
      spaceModal,
      gettingStartedPath,
      confirmAccess,
      createFirstBroadcast,
    ],
  );

  return {
    stepActions: actions,
  };
};
